/**
 * Google Maps のポリライン（Leafletの Polyline 相当）
 *
 * @vis.gl/react-google-maps にポリラインのコンポーネントは無いため、
 * google.maps.Polyline を命令的に生成・破棄するラッパーとして実装する。
 */

import { useMap, useMapsLibrary } from '@vis.gl/react-google-maps'
import { useEffect, useRef, useState } from 'react'

/** 破線表現用のシンボル（Leafletの dashArray 相当） */
const DASH_SYMBOL = { path: 'M 0,-1 0,1', strokeOpacity: 1, scale: 3 }
const DASH_REPEAT = '16px'

type LatLng = google.maps.LatLngLiteral

export type GooglePolylineProps = {
  path: LatLng[]
  strokeColor: string
  strokeWeight?: number
  strokeOpacity?: number
  /** true にすると破線で描画する */
  dashed?: boolean
  zIndex?: number
  clickable?: boolean
  onClick?: (latLng: LatLng) => void
  onMouseMove?: (latLng: LatLng) => void
  onMouseOut?: () => void
}

type Handlers = Pick<GooglePolylineProps, 'onClick' | 'onMouseMove' | 'onMouseOut'>

const toLatLng = (event: google.maps.MapMouseEvent): LatLng | null => {
  const latLng = event.latLng
  if (!latLng) return null
  return { lat: latLng.lat(), lng: latLng.lng() }
}

export function GooglePolyline({
  path,
  strokeColor,
  strokeWeight = 6,
  strokeOpacity = 1,
  dashed = false,
  zIndex,
  clickable = true,
  onClick,
  onMouseMove,
  onMouseOut,
}: GooglePolylineProps) {
  const map = useMap()
  const mapsLibrary = useMapsLibrary('maps')
  const [polyline, setPolyline] = useState<google.maps.Polyline | null>(null)

  // ハンドラは毎レンダリング新しい関数になるため、refで受け渡してリスナーの貼り直しを避ける
  const handlersRef = useRef<Handlers>({ onClick, onMouseMove, onMouseOut })
  useEffect(() => {
    handlersRef.current = { onClick, onMouseMove, onMouseOut }
  }, [onClick, onMouseMove, onMouseOut])

  useEffect(() => {
    if (!map || !mapsLibrary) return

    const instance = new mapsLibrary.Polyline()
    instance.setMap(map)
    setPolyline(instance)

    return () => {
      instance.setMap(null)
      setPolyline(null)
    }
  }, [map, mapsLibrary])

  useEffect(() => {
    polyline?.setPath(path)
  }, [polyline, path])

  useEffect(() => {
    if (!polyline) return

    // 破線は本体の線を透明にしてシンボルの繰り返しで表現する
    polyline.setOptions({
      strokeColor,
      strokeWeight,
      strokeOpacity: dashed ? 0 : strokeOpacity,
      zIndex,
      clickable,
      icons: dashed ? [{ icon: { ...DASH_SYMBOL, scale: strokeWeight / 2 }, offset: '0', repeat: DASH_REPEAT }] : null,
    })
  }, [polyline, strokeColor, strokeWeight, strokeOpacity, dashed, zIndex, clickable])

  useEffect(() => {
    if (!polyline) return

    const listeners = [
      polyline.addListener('click', (event: google.maps.MapMouseEvent) => {
        const latLng = toLatLng(event)
        if (latLng) handlersRef.current.onClick?.(latLng)
      }),
      polyline.addListener('mousemove', (event: google.maps.MapMouseEvent) => {
        const latLng = toLatLng(event)
        if (latLng) handlersRef.current.onMouseMove?.(latLng)
      }),
      polyline.addListener('mouseout', () => {
        handlersRef.current.onMouseOut?.()
      }),
    ]

    return () => {
      listeners.forEach((listener) => listener.remove())
    }
  }, [polyline])

  return null
}
